import { API_BUSINESS_URL } from '@/utils';

import { Flow, FlowStep, StepResult } from '../models';
import { interpolate, readPath, stringifyCaptured } from './template';
import { pngPlaceholder } from './png';

export interface RunContext {
  sessionToken: string;
  variables: Record<string, string>;
}

export interface RunHandlers {
  onStepStart: (result: StepResult) => void;
  onStepEnd: (result: StepResult, variables: Record<string, string>) => void;
}

const resolveUrl = (path: string): string =>
  /^https?:\/\//i.test(path) ? path : `${API_BUSINESS_URL}${path}`;

const statusAllowed = (status: number, expected: string): boolean => {
  const wanted = expected
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);

  if (!wanted.length) return status >= 200 && status < 300;
  return wanted.includes(String(status));
};

const buildBody = (
  step: FlowStep,
  variables: Record<string, string>
): { body: BodyInit | undefined; preview: string; contentType?: string } => {
  if (step.bodyMode === 'json' && step.body.trim()) {
    const preview = interpolate(step.body, variables);
    return { body: preview, preview, contentType: 'application/json' };
  }

  if (step.bodyMode === 'multipart') {
    const form = new FormData();
    const lines: string[] = [];

    step.fields.forEach((field) => {
      const name = interpolate(field.name, variables);
      if (field.kind === 'png') {
        form.append(name, pngPlaceholder(name));
        lines.push(`${name}: <png 1x1>`);
        return;
      }
      const value = interpolate(field.value, variables);
      form.append(name, value);
      lines.push(`${name}: ${value}`);
    });

    return { body: form, preview: lines.join('\n') };
  }

  return { body: undefined, preview: '' };
};

export const runFlow = async (
  flow: Flow,
  context: RunContext,
  handlers: RunHandlers,
  shouldStop: () => boolean
): Promise<Record<string, string>> => {
  let variables = { ...context.variables };

  for (const step of flow.steps) {
    if (!step.enabled) continue;

    const url = resolveUrl(interpolate(step.path, variables));
    const running: StepResult = {
      stepId: step.id,
      name: step.name,
      method: step.method,
      url,
      state: 'running',
      status: null,
      ms: 0,
      requestBody: '',
      response: '',
      captured: {},
      error: '',
    };
    handlers.onStepStart(running);

    const startedAt = performance.now();
    const { body, preview, contentType } = buildBody(step, variables);

    const headers: Record<string, string> = {
      Authorization: `Bearer ${context.sessionToken}`,
    };
    Object.entries(step.headers).forEach(([key, value]) => {
      headers[key] = interpolate(value, variables);
    });
    if (contentType) headers['Content-Type'] = contentType;

    let status: number | null = null;
    let responseText = '';
    let error = '';

    try {
      const response = await fetch(url, { method: step.method, headers, body });
      status = response.status;
      responseText = await response.text();
    } catch (requestError) {
      /*
       * Un fetch que falla por red o por CORS tira TypeError y NO deja ver el
       * status: el navegador se lo esconde a JS. Decirlo asi evita buscar el
       * error en el back cuando la respuesta ni siquiera llego al codigo.
       */
      error =
        requestError instanceof TypeError
          ? 'El navegador bloqueo la respuesta (red o CORS). El status real solo se ve en la pestana Network.'
          : requestError instanceof Error
            ? requestError.message
            : 'La peticion no llego a destino';
    }

    const ms = Math.round(performance.now() - startedAt);
    const captured: Record<string, string> = {};
    let parsed: unknown = null;

    if (responseText) {
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
    }

    if (parsed !== null) {
      step.captures.forEach(({ name, path }) => {
        if (!name) return;
        const value = stringifyCaptured(readPath(parsed, path));
        captured[name] = value;
      });
    }

    variables = { ...variables, ...captured };

    const ok = status !== null && statusAllowed(status, step.expectStatus);
    if (!ok && !error) {
      error = `Se esperaba ${step.expectStatus || '2xx'} y respondió ${status}`;
    }

    const missingCaptures = step.captures
      .filter(({ name }) => name && !captured[name])
      .map(({ name }) => name);
    if (ok && missingCaptures.length) {
      error = `No se pudo capturar: ${missingCaptures.join(', ')}`;
    }

    handlers.onStepEnd(
      {
        ...running,
        state: ok && !error ? 'ok' : 'failed',
        status,
        ms,
        requestBody: preview,
        response: responseText,
        captured,
        error,
      },
      variables
    );

    if (error || shouldStop()) break;
  }

  return variables;
};
